import { useCallback } from "react";
import { filterSearch } from "@/lib/columnFilters";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { UsersClient } from "@/features/users/UsersClient";
import {
  listUsers,
  listRoles,
  usersKeys,
  type UsersSearch,
} from "@/features/users/users.api";
import { applyParamUpdates } from "@/lib/searchParams";

export const Route = createFileRoute("/_shell/users")({
  // Admin-only. Server-side RBAC enforces this too; this is the UI gate that
  // replaces the Next.js page's `redirect("/dashboard")`.
  beforeLoad: ({ context }) => {
    if (!context.session.roles.includes("admin")) {
      throw redirect({ to: "/dashboard" });
    }
  },
  validateSearch: (search: Record<string, unknown>): UsersSearch => ({
    ...filterSearch(search),
    q: typeof search.q === "string" && search.q ? search.q : undefined,
    role: typeof search.role === "string" && search.role ? search.role : undefined,
    isActive:
      typeof search.isActive === "string" && search.isActive
        ? search.isActive
        : undefined,
    cursor:
      typeof search.cursor === "string" && search.cursor
        ? search.cursor
        : undefined,
    limit: typeof search.limit === "string" && search.limit ? search.limit : undefined,
    sort: typeof search.sort === "string" && search.sort ? search.sort : undefined,
    order: typeof search.order === "string" && search.order ? search.order : undefined,
  }),
  component: UsersScreen,
});

function UsersScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();

  const usersQuery = useQuery({
    queryKey: usersKeys.list(search),
    queryFn: () => listUsers(search),
    placeholderData: keepPreviousData,
  });
  const rolesQuery = useQuery({
    queryKey: usersKeys.roles,
    queryFn: listRoles,
  });

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({
        search: (prev) => applyParamUpdates(prev, updates),
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <div className="wl-page wl-page--list" data-screen-label="Back-office Users">
      <UsersClient
        initialUsers={usersQuery.data?.data ?? []}
        pagination={
          usersQuery.data?.pagination ?? {
            limit: 50,
            hasNextPage: false,
            nextCursor: null,
          }
        }
        roles={rolesQuery.data ?? []}
        search={search}
        updateParams={updateParams}
        userId={session.userId}
        isFetching={usersQuery.isFetching}
        isLoading={usersQuery.isLoading}
      />
    </div>
  );
}
